import React from 'react'
import logger from '../utils/logger'
import { onResume } from '../lib/lifecycle'

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    logger.error('ErrorBoundary caught:', error)
    logger.capture(error, { componentStack: info?.componentStack })
  }

  componentDidMount() {
    // If a render error fires once during a botched resume, the next resume
    // automatically clears the error UI so the user isn't stuck. The next
    // render attempt either succeeds (cached data is now visible) or throws
    // again and lands back in this state — either way no manual tap needed.
    this._unsubResume = onResume(() => {
      if (this.state.hasError) {
        logger.warn('ErrorBoundary auto-reset on resume', { errorMsg: this.state.error?.message })
        this.setState({ hasError: false, error: null })
      }
    })
  }

  componentWillUnmount() {
    if (this._unsubResume) this._unsubResume()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          color: 'var(--text-primary, #fff)',
          backgroundColor: 'var(--bg-primary, #1a1a2e)',
          minHeight: '50vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Something went wrong</h2>
          <p style={{ color: 'var(--text-secondary, #aaa)', fontSize: '0.875rem', maxWidth: '320px' }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '9999px',
              border: 'none',
              backgroundColor: 'var(--accent-primary, #4f46e5)',
              color: '#fff',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '9999px',
              border: '1px solid var(--border-color, #333)',
              backgroundColor: 'transparent',
              color: 'var(--text-secondary, #aaa)',
              cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            Reload Page
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
